import type * as ImagePicker from 'expo-image-picker';

export type InAppCameraRequest = {
  requestId: string;
  options: ImagePicker.ImagePickerOptions;
  telemetry: Record<string, unknown>;
};

export type InAppCameraInput = Omit<InAppCameraRequest, 'requestId'>;

export type InAppCameraResult =
  | { status: 'success'; asset: ImagePicker.ImagePickerAsset }
  | { status: 'cancelled' }
  | { status: 'denied' }
  | { status: 'failed'; error: Error };

type PendingRequest = InAppCameraRequest & {
  resolve: (result: InAppCameraResult) => void;
  acknowledged: boolean;
  handshakeTimeout: ReturnType<typeof setTimeout> | null;
};

type RequestListener = (request: InAppCameraRequest | null) => void;

let pendingRequest: PendingRequest | null = null;
const listeners = new Set<RequestListener>();
const HANDSHAKE_TIMEOUT_MS = 5_000;

const createRequestId = (): string =>
  `in-app-camera-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const clearHandshakeTimeout = (request: PendingRequest): void => {
  if (request.handshakeTimeout) {
    clearTimeout(request.handshakeTimeout);
    request.handshakeTimeout = null;
  }
};

const toPublicRequest = (request: PendingRequest): InAppCameraRequest => ({
  requestId: request.requestId,
  options: request.options,
  telemetry: request.telemetry,
});

const notifyListeners = (): void => {
  const request = pendingRequest
    ? toPublicRequest(pendingRequest)
    : null;

  listeners.forEach(listener => listener(request));
};

export const requestInAppCamera = (
  request: InAppCameraInput
): Promise<InAppCameraResult> => {
  if (pendingRequest) {
    if (!pendingRequest.acknowledged) {
      const staleRequest = pendingRequest;
      pendingRequest = null;
      clearHandshakeTimeout(staleRequest);
      staleRequest.resolve({
        status: 'failed',
        error: new Error('A solicitação anterior da câmera não foi consumida pelo host da câmera interna.'),
      });
      notifyListeners();
    } else {
      return Promise.resolve({
        status: 'failed',
        error: new Error('Já existe uma captura de câmera em andamento.'),
      });
    }
  }

  return new Promise(resolve => {
    const requestId = createRequestId();
    const nextRequest: PendingRequest = {
      ...request,
      requestId,
      resolve,
      acknowledged: false,
      handshakeTimeout: null,
    };
    nextRequest.handshakeTimeout = setTimeout(() => {
      if (pendingRequest?.requestId !== requestId || pendingRequest.acknowledged) {
        return;
      }

      pendingRequest = null;
      clearHandshakeTimeout(nextRequest);
      resolve({
        status: 'failed',
        error: new Error('O host da câmera interna não foi montado a tempo.'),
      });
      notifyListeners();
    }, HANDSHAKE_TIMEOUT_MS);
    pendingRequest = nextRequest;
    notifyListeners();
  });
};

export const acknowledgeInAppCamera = (requestId: string): void => {
  if (!pendingRequest || pendingRequest.requestId !== requestId) {
    return;
  }

  pendingRequest.acknowledged = true;
  clearHandshakeTimeout(pendingRequest);
};

export const subscribeInAppCamera = (listener: RequestListener): (() => void) => {
  listeners.add(listener);
  listener(pendingRequest ? toPublicRequest(pendingRequest) : null);

  return () => {
    listeners.delete(listener);
  };
};

export const resolveInAppCamera = (
  requestId: string,
  result: InAppCameraResult
): void => {
  const request = pendingRequest;
  if (!request || request.requestId !== requestId) {
    return;
  }

  pendingRequest = null;
  clearHandshakeTimeout(request);
  request.resolve(result);
  notifyListeners();
};
